// RevoGrid cell renderers: a searchable Product picker (name + distinguishing properties) and
// single-click Location/Unit dropdowns, with inline styles and a raw-value fallback for unresolved
// cells.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from 'react-tiny-popover';
import {
  getCatalog,
  namesEveryWord,
  productById,
  productLabel,
  searchWordsOf,
} from '~/pages/inventory-import/lib/catalog';
import {
  getLocations,
  locationLabel,
} from '~/pages/inventory-import/lib/locationCatalog';
import { diceCoefficient } from '~/pages/inventory-import/lib/similarity';
import type { GridRow } from '~/pages/inventory-import/lib/rows';
import type {
  Candidate,
  CatalogProduct,
} from '~/pages/inventory-import/lib/types';
import {
  isUnitAllowedForCategory,
  unitOptionsForCategory,
} from '~/utils/productUnitPolicy';

// RevoGrid renders these cells via React roots inside the grid, where Tailwind classes don't
// reach — but CSS custom properties inherit through, so reference the design tokens via var().
const HOVER_BG = 'var(--color-neut-hover)';
const SELECTED_BG = 'var(--color-prim-50)';
const MUTED = 'var(--color-neut-sec)';
const SURFACE = 'var(--color-neut-0)';
const BORDER = 'var(--color-neut-border)';

interface Handlers {
  onDelete: (id: number) => void;
  onPick: (id: number, productId: string) => void;
  onSet: (id: number, field: string, value: string) => void;
}

interface CellProps {
  model?: GridRow;
  handlers?: { current: Handlers };
}

const baseCellBox: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  height: '100%',
  padding: '0 8px',
  boxSizing: 'border-box',
};

const productTriggerStyle: CSSProperties = {
  ...baseCellBox,
  justifyContent: 'space-between',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  font: 'inherit',
  textAlign: 'left',
};

const deleteCellBox: CSSProperties = {
  ...baseCellBox,
  justifyContent: 'center',
};

const deleteCellButton: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  color: 'var(--color-error-600)',
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  padding: 4,
};

const selectStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  border: 'none',
  background: 'transparent',
  padding: '0 6px',
  cursor: 'pointer',
  font: 'inherit',
};

const popoverPanelStyle: CSSProperties = {
  width: 340,
  maxHeight: 300,
  display: 'flex',
  flexDirection: 'column',
  background: SURFACE,
  border: '1px solid var(--color-neut-300)',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  overflow: 'hidden',
  fontSize: 13,
};

const searchInputStyle: CSSProperties = {
  padding: '8px 10px',
  border: 'none',
  borderBottom: `1px solid ${BORDER}`,
};

const ellipsis: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Wraps long content to multiple lines (the row grows to fit via rowDefinitions). No overflow
 * clipping — the row is sized to fit, and clipping cut the bottom of italic text in short rows. */
const wrapText: CSSProperties = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  textAlign: 'left',
  lineHeight: 1.4,
};

const chevronStyle: CSSProperties = { color: MUTED, flexShrink: 0 };

const MAX_OPTIONS = 50;
// Minimum relevance for a product to appear in the search results. scoreProduct returns
// 0–2 (a substring or every-word hit adds 1, plus a 0–1 dice similarity), so 0.15 keeps
// loosely-similar names while dropping near-unrelated ones.
const MIN_MATCH_SCORE = 0.15;

function stop(e: { stopPropagation: () => void }) {
  e.stopPropagation();
}

/** Relevance of a product for a search query (name + properties; a full hit strongly boosts). */
function scoreProduct(
  query: string,
  searched: string[],
  product: CatalogProduct
): number {
  const hay = `${product.searchName} ${product.propertySummary.toLowerCase()}`;
  const hit = hay.includes(query) || namesEveryWord(product, searched);
  return (hit ? 1 : 0) + diceCoefficient(query, product.searchName);
}

/** Option list: candidates first when no query, else fuzzy-ranked across the catalog by name. */
function optionsFor(candidates: Candidate[], query: string): CatalogProduct[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    const suggested = candidates.map((c) => c.product);
    const suggestedIds = new Set(suggested.map((p) => p.id));
    const out: CatalogProduct[] = [...suggested];
    for (const p of getCatalog()) {
      if (out.length >= MAX_OPTIONS) break;
      if (!suggestedIds.has(p.id)) out.push(p);
    }
    return out.slice(0, MAX_OPTIONS);
  }
  const searched = searchWordsOf(q);
  const scored: { product: CatalogProduct; score: number }[] = [];
  for (const product of getCatalog()) {
    const score = scoreProduct(q, searched, product);
    if (score > MIN_MATCH_SCORE) scored.push({ product, score });
  }
  scored.sort((a, b) => b.score - a.score);
  const out: CatalogProduct[] = [];
  for (const s of scored) {
    if (out.length >= MAX_OPTIONS) break;
    out.push(s.product);
  }
  return out;
}

/** A two-line option: product name on top, distinguishing properties beneath. */
function Option({
  label,
  sublabel,
  selected,
  onPick,
}: {
  label: string;
  sublabel?: string;
  selected: boolean;
  onPick: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onPick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '6px 10px',
        border: 'none',
        cursor: 'pointer',
        background: hover ? HOVER_BG : selected ? SELECTED_BG : SURFACE,
      }}
    >
      <div style={{ ...ellipsis, color: 'var(--color-neut-prim)' }}>
        {label}
      </div>
      {sublabel && (
        <div style={{ ...ellipsis, color: MUTED, fontSize: 12 }}>
          {sublabel}
        </div>
      )}
    </button>
  );
}

/** Popover body: search box + two-line product options. Focuses the search box on open. */
function ProductPanel({
  productId,
  candidates,
  onPick,
}: {
  productId: string;
  candidates: Candidate[];
  onPick: (id: string) => void;
}) {
  const { t } = useTranslation('inventory-import');
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const total = getCatalog().length;
  const list = optionsFor(candidates, query);
  const truncated = list.length < total && !query;

  return (
    <div style={popoverPanelStyle}>
      <input
        ref={inputRef}
        aria-label={t('cells.search_products_aria')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('cells.search_products_placeholder', { count: total })}
        style={searchInputStyle}
      />
      <div style={{ overflowY: 'auto', background: SURFACE }}>
        <Option
          label={t('cells.no_match')}
          selected={!productId}
          onPick={() => onPick('')}
        />
        {list.map((p) => (
          <Option
            key={p.id}
            label={p.name}
            sublabel={p.propertySummary}
            selected={p.id === productId}
            onPick={() => onPick(p.id)}
          />
        ))}
      </div>
      {truncated && (
        <div
          style={{
            padding: '6px 10px',
            borderTop: `1px solid ${BORDER}`,
            color: MUTED,
            fontSize: 12,
          }}
        >
          {t('cells.showing_of_total', { visible: list.length, total })}
        </div>
      )}
    </div>
  );
}

/** Resolved-product cell: single click opens the searchable picker; raw value shown when unmatched. */
export function ProductCell(props: Record<string, unknown>) {
  const { t } = useTranslation('inventory-import');
  const { model, handlers } = props as CellProps;
  const [open, setOpen] = useState(false);
  const productId = String(model?.product ?? '');
  const resolved = productId.length > 0;
  const raw = String(model?.descriptor ?? '');
  const display = resolved ? productLabel(productId) : raw;
  const candidates = model?.__candidates ?? [];

  const pick = (chosen: string) => {
    if (model && handlers) handlers.current.onPick(model.__id, chosen);
    setOpen(false);
  };

  return (
    <Popover
      isOpen={open}
      positions={['bottom', 'top']}
      align="start"
      padding={2}
      containerStyle={{ zIndex: '1000' }}
      onClickOutside={() => setOpen(false)}
      content={
        <ProductPanel
          productId={productId}
          candidates={candidates}
          onPick={pick}
        />
      }
    >
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('cells.pick_product_aria')}
        title={t('cells.pick_product_title')}
        style={productTriggerStyle}
        onClick={() => setOpen((o) => !o)}
      >
        <span
          style={{
            ...wrapText,
            color: resolved
              ? 'var(--color-neut-prim)'
              : 'var(--color-neut-400)',
            fontStyle: resolved ? 'normal' : 'italic',
          }}
        >
          {display || t('cells.select_product')}
        </span>
        <span style={chevronStyle}>▾</span>
      </button>
    </Popover>
  );
}

/** Dropdown cell for a field with predefined values (e.g. Unit). Single-click native select. */
export function EnumCell(props: Record<string, unknown>) {
  const { t } = useTranslation('inventory-import');
  const { model, handlers, field, options } = props as CellProps & {
    field?: string;
    options?: string[];
  };
  const value = field && model ? String(model[field] ?? '') : '';
  const product = productById(String(model?.product ?? ''));
  const productCategory = product ? product.category : undefined;
  const visibleOptions =
    field === 'unit'
      ? unitOptionsForCategory(productCategory, options ?? [], value)
      : (options ?? []);
  return (
    <select
      aria-label={
        field
          ? t('cells.select_field_aria', { field: t(`fields.${field}`) })
          : t('cells.select_value_aria')
      }
      value={value}
      onMouseDown={stop}
      onClick={stop}
      onChange={(e) => {
        if (model && handlers && field)
          handlers.current.onSet(model.__id, field, e.target.value);
      }}
      style={selectStyle}
    >
      <option value="">—</option>
      {visibleOptions.map((o) => (
        <option
          key={o}
          value={o}
          disabled={
            field === 'unit' && !isUnitAllowedForCategory(productCategory, o)
          }
        >
          {o}
        </option>
      ))}
    </select>
  );
}

/** Resolved-location cell: single-click dropdown of the org's locations (matched by code/name). */
/** Popover body: search box + location options. Focuses the search box on open. */
function LocationPanel({
  value,
  onPick,
}: {
  value: string;
  onPick: (code: string) => void;
}) {
  const { t } = useTranslation('inventory-import');
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const q = query.trim().toLowerCase();
  const list = q
    ? getLocations().filter((l) =>
        `${l.code} ${l.displayName ?? l.name}`.toLowerCase().includes(q)
      )
    : getLocations();

  return (
    <div style={popoverPanelStyle}>
      <input
        ref={inputRef}
        aria-label={t('cells.search_locations_aria')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('cells.search_locations_placeholder')}
        style={searchInputStyle}
      />
      <div style={{ overflowY: 'auto', background: SURFACE }}>
        <Option
          label={t('cells.no_location')}
          selected={!value}
          onPick={() => onPick('')}
        />
        {list.map((l) => (
          <Option
            key={l.id}
            label={l.displayName ?? l.name}
            sublabel={l.code}
            selected={l.code === value}
            onPick={() => onPick(l.code)}
          />
        ))}
      </div>
    </div>
  );
}

/** Resolved-location cell: single click opens the picker; raw value shown when unmatched. */
export function LocationCell(props: Record<string, unknown>) {
  const { t } = useTranslation('inventory-import');
  const { model, handlers } = props as CellProps;
  const [open, setOpen] = useState(false);
  const value = String(model?.location ?? '');
  const resolved = value.length > 0;
  const raw = String(model?.locationCode ?? '');
  const display = resolved ? locationLabel(value) : raw;

  const pick = (code: string) => {
    if (model && handlers) handlers.current.onSet(model.__id, 'location', code);
    setOpen(false);
  };

  return (
    <Popover
      isOpen={open}
      positions={['bottom', 'top']}
      align="start"
      padding={2}
      containerStyle={{ zIndex: '1000' }}
      onClickOutside={() => setOpen(false)}
      content={<LocationPanel value={value} onPick={pick} />}
    >
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('cells.select_location_aria')}
        style={productTriggerStyle}
        onClick={() => setOpen((o) => !o)}
      >
        <span
          style={{
            ...wrapText,
            color: resolved
              ? 'var(--color-neut-prim)'
              : 'var(--color-neut-400)',
            fontStyle: resolved ? 'normal' : 'italic',
          }}
        >
          {display || t('cells.select_location')}
        </span>
        <span style={chevronStyle}>▾</span>
      </button>
    </Popover>
  );
}

/** Per-row delete button. */
export function DeleteCell(props: Record<string, unknown>) {
  const { t } = useTranslation('inventory-import');
  const { model, handlers } = props as CellProps;
  return (
    <div style={deleteCellBox}>
      <button
        type="button"
        title={t('cells.delete_row')}
        aria-label={t('cells.delete_row')}
        onClick={() => {
          if (model && handlers) handlers.current.onDelete(model.__id);
        }}
        style={deleteCellButton}
      >
        ✕
      </button>
    </div>
  );
}
